import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Modal } from '@fiado/react-kit';
import { integrationsApi } from '../../../../api/endpoints/integrations';
import { tenantApi } from '../../../../api/endpoints/tenant';
import { handleApiError } from '../../../../api/errorMapping';
import { integrationsKey } from '../../../../hooks/useIntegrations';
import { Label } from '../../../../components/ui/label';
import { Select } from '../../../../components/ui/select';
import { NOT_ASSIGNABLE_EXPLANATION, notAssignableReason } from './assignableRoles';

// IP suelta, CIDR o el comodín. La validación exacta la hace el authorizer con ipaddr.js.
const CIDR_REGEX = /^(\*|[0-9a-fA-F.:]+(\/\d{1,3})?)$/;

const parseCidrs = (raw: string): string[] =>
    raw
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);

// La validación de CIDR vive en el schema y no en el mutationFn: así se pinta INLINE en el campo.
// Lanzada desde el mutationFn caía en handleApiError, que descarta el mensaje y muestra un genérico.
const schema = z.object({
    displayName: z.string().trim().min(1, 'Ponle un nombre a la integración.'),
    roleId: z.string().min(1, 'Elige el rol que va a portar.'),
    ipAllowlist: z.string().superRefine((raw, ctx) => {
        const invalidEntry = parseCidrs(raw).find((c) => !CIDR_REGEX.test(c));
        if (invalidEntry) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `"${invalidEntry}" no parece una IP ni un CIDR válido.`,
            });
        }
    }),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    tenantId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateIntegrationModal({ tenantId, open, onOpenChange }: Props) {
    const queryClient = useQueryClient();

    const { data: roles } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'roles'],
        queryFn: () => tenantApi.listRoles(tenantId),
        enabled: open,
    });

    // Los no asignables se muestran deshabilitados CON el motivo: esconderlos genera tickets.
    const { assignable, blocked } = useMemo(() => {
        const items = roles?.items ?? [];
        return {
            assignable: items.filter((r) => notAssignableReason(r) === null),
            blocked: items
                .map((r) => ({ role: r, reason: notAssignableReason(r) }))
                .filter(
                    (x): x is { role: (typeof items)[number]; reason: 'ADMIN' | 'PLATFORM' } =>
                        x.reason !== null,
                ),
        };
    }, [roles]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { displayName: '', roleId: '', ipAllowlist: '' },
    });

    const createMut = useMutation({
        mutationFn: (values: FormValues) => {
            const cidrs = parseCidrs(values.ipAllowlist);
            return integrationsApi.create(
                tenantId,
                {
                    displayName: values.displayName.trim(),
                    roleId: values.roleId,
                    ipAllowlist: cidrs.length > 0 ? cidrs : undefined,
                },
                // Una key por intento: protege del doble clic sin bloquear un reintento tras error.
                { idempotencyKey: crypto.randomUUID() },
            );
        },
        onSuccess: async () => {
            toast.success('Integración creada. Ahora emítele una llave.');
            await queryClient.invalidateQueries({ queryKey: integrationsKey(tenantId) });
            reset();
            onOpenChange(false);
        },
        onError: (error) => {
            handleApiError(error);
        },
    });

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            title="Nueva integración"
            size="md"
        >
            <form
                className="space-y-4"
                onSubmit={(e) =>
                    void handleSubmit((values) => {
                        createMut.mutate(values);
                    })(e)
                }
            >
                <Input
                    label="Nombre"
                    placeholder="La Central"
                    error={errors.displayName?.message}
                    hint="Cómo la vas a reconocer en esta lista."
                    {...register('displayName')}
                />

                <div className="space-y-1">
                    <Label htmlFor="roleId">Rol</Label>
                    <Select id="roleId" {...register('roleId')}>
                        <option value="">Elige un rol…</option>
                        {assignable.map((r) => (
                            <option key={r.roleId} value={r.roleId}>
                                {r.roleName}
                            </option>
                        ))}
                        {blocked.map(({ role }) => (
                            <option key={role.roleId} value={role.roleId} disabled>
                                {role.roleName} — no disponible
                            </option>
                        ))}
                    </Select>
                    {errors.roleId?.message && (
                        <p className="text-sm text-destructive">{errors.roleId.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                        Los permisos de la integración son exactamente los de este rol. Se elige de
                        los roles que ya existen en el tenant.
                    </p>
                    {blocked.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                            {blocked.map(({ role, reason }) => (
                                <li key={role.roleId}>
                                    <span className="font-medium">{role.roleName}:</span>{' '}
                                    {NOT_ASSIGNABLE_EXPLANATION[reason]}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="space-y-1">
                    <Label htmlFor="ipAllowlist">IPs permitidas (opcional)</Label>
                    <textarea
                        id="ipAllowlist"
                        rows={3}
                        placeholder={'189.128.0.0/16\n201.20.30.40'}
                        className="w-full rounded-md border border-border bg-background p-2 font-mono text-sm"
                        {...register('ipAllowlist')}
                    />
                    {errors.ipAllowlist?.message && (
                        <p className="text-sm text-destructive">{errors.ipAllowlist.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                        Una por línea. Si lo dejas vacío o pones <code>*</code>, la llave funciona
                        desde cualquier IP — la lista te lo va a marcar.
                    </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={createMut.isPending}>
                        Crear integración
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
